import Vue from "vue";
import lodashGet from "lodash/get";
import { v4 as uuidv4 } from "uuid";

const cuilGen = function(numeroDocumento, sexo) {
  /**
   * Cuil format is: AB - document_number - C
   * Author: Nahuel Sanchez, Woile
   *
   * @param {str} document_number -> string solo digitos
   * @param {str} gender -> debe contener HOMBRE, MUJER o SOCIEDAD
   *
   * @return {str}
   **/
  let HOMBRE = ["HOMBRE", "M", "MALE"];
  let MUJER = ["MUJER", "F", "FEMALE"];
  let SOCIEDAD = ["SOCIEDAD", "S", "SOCIETY"];
  let AB, C;

  /**
   * Verifico que el document_number tenga exactamente ocho numeros y que
   * la cadena no contenga letras.
   */
  if (numeroDocumento.length !== 8 || isNaN(numeroDocumento)) {
    if (numeroDocumento.length === 7 && !isNaN(numeroDocumento)) {
      numeroDocumento = "0".concat(numeroDocumento);
    } else {
      // Muestro un error en caso de no serlo.
      Vue.$log.error("El numero de documento ingresado no es correcto.");
      return "";
    }
  }

  /**
   * De esta manera permitimos que el gender venga en minusculas,
   * mayusculas y titulo.
   */
  sexo = sexo.toUpperCase();

  // Defino el valor del prefijo.
  if (HOMBRE.indexOf(sexo) >= 0) {
    AB = "20";
  } else if (MUJER.indexOf(sexo) >= 0) {
    AB = "27";
  } else {
    AB = "30";
  }

  /*
   * Los numeros (excepto los dos primeros) que le tengo que
   * multiplicar a la cadena formada por el prefijo y por el
   * numero de document_number los tengo almacenados en un arreglo.
   */
  let multiplicadores = [3, 2, 7, 6, 5, 4, 3, 2];

  // Realizo las dos primeras multiplicaciones por separado.
  let calculo = parseInt(AB.charAt(0)) * 5 + parseInt(AB.charAt(1)) * 4;

  /*
   * Recorro el arreglo y el numero de document_number para
   * realizar las multiplicaciones.
   */
  for (let i = 0; i < 8; i++) {
    calculo += parseInt(numeroDocumento.charAt(i)) * multiplicadores[i];
  }

  // Calculo el resto.
  let resto = parseInt(calculo) % 11;

  /*
   * Llevo a cabo la evaluacion de las tres condiciones para
   * determinar el valor de C y conocer el valor definitivo de
   * AB.
   */
  if (SOCIEDAD.indexOf(sexo) < 0 && resto === 1) {
    if (HOMBRE.indexOf(sexo) >= 0) {
      C = "9";
    } else {
      C = "4";
    }
    AB = "23";
  } else if (resto === 0) {
    C = "0";
  } else {
    C = 11 - resto;
  }

  // Generate cuit
  return [AB, "-", numeroDocumento, "-", C].join("");
};

const replaceAll = function(str, search, replacement) {
  return str.split(search).join(replacement);
};

const getParameterByName = (name, url) => {
  if (!url) url = window.location.href;
  let results = new RegExp(
    "[?&]" + name.replace(/[[\]]/g, "\\$&") + "(=([^&#]*)|&|#|$)"
  ).exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
};

const getTrackingCodeLocal = name => {
  let tc = getParameterByName(name, window.location.href);

  if (tc != null && tc !== "") {
    return tc;
  }
  return null;
};

const obtenerTrackingCode = function() {
  let keys = ["cid", "onsite", "ema", "psm", "dis", "afi", "afl"];
  let tc = "";

  for (let ndx = 0; ndx < keys.length; ndx++) {
    let key = keys[ndx];
    let tccan = getTrackingCodeLocal(key);
    if (tccan !== null) {
      tc = tccan;
      break;
    }
  }

  if (tc === "") {
    return "";
  }

  let tcSplit = "";
  let tcBuild = "";

  if (tc.indexOf(":") === -1) {
    // Tracking codes viejos
    tcSplit = tc.split("-");
  } else {
    // Tracking codes nuevos
    replaceAll(tc, "_", " ");
    tcSplit = tc.split("-")[0].split(":");
  }

  tcBuild =
    tcSplit[0].trim() +
    "-" +
    tcSplit[1].trim() +
    "-" +
    tcSplit[2].trim() +
    "-" +
    tcSplit[3].trim();

  return "-" + tcBuild;
};

const toTitleCase = function(str) {
  if (!!str && str.trim() !== "") {
    return str.replace(
      /\w\S*/g,
      txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  }
  return str;
};

const isDev = function() {
  return PRODUCTION;
};

const isValidDate = function(dateString, minAge, maxAge) {
  // Verificar patrón
  let regex_date = /\d{2}\/\d{2}\/\d{4}/;

  if (!regex_date.test(dateString)) {
    return false;
  }

  // Parsear string de fecha
  let parts = dateString.split("/");
  let day = parseInt(parts[0], 10);
  let month = parseInt(parts[1], 10);
  let year = parseInt(parts[2], 10);

  // Rango de meses y años
  if (year < 1000 || year > 3000 || month == 0 || month > 12) {
    return false;
  }

  // Ver edades permitidas
  if (minAge || maxAge) {
    const clientBirth = new Date(`${year}-${month}-${day}`);
    const month_diff = Date.now() - clientBirth.getTime();
    const age_dt = new Date(month_diff);
    const clientAge = Math.abs(age_dt.getUTCFullYear() - 1970);
    if (minAge && clientAge < minAge) {
      return false;
    }
    if (maxAge && clientAge >= maxAge) {
      return false;
    }
  }

  const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Ajustar años biciestos
  if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0)) {
    monthLength[1] = 29;
  }

  // Verificar rango de días
  return day > 0 && day <= monthLength[month - 1];
};

/**
 * Función de orden superior de lodash.get
 * que convierte null a undefined
 */
const get = (o, path, defval) => {
  return lodashGet(o, path, defval) === null
    ? defval
    : lodashGet(o, path, defval);
};

/**
 * Formatear un numero como dinero
 * @param {Number} num Monto dinero en entero
 */
const formatDinero = num => {
  return new Intl.NumberFormat("es-AR").format(num);
};

const isValidCuil = function(numeroCuil) {
  if (!numeroCuil) {
    return false;
  }

  let cuil = numeroCuil + "";

  if (/^[a-zA-Z]+$/.test(cuil)) {
    // "El campo debe ser numérico";
    return false;
  }

  if (cuil.length !== 11) {
    // Longitud inválida
    return false;
  }

  let acumulado = 0;
  let digitos = cuil.split("");
  let digito = parseInt(digitos.pop());

  for (let i = 0; i < digitos.length; i++) {
    acumulado += parseInt(digitos[9 - i]) * (2 + (i % 6));
  }

  let verif = 11 - (acumulado % 11);
  if (verif === 11) {
    verif = 0;
  }

  let validFormat = digito === verif;
  if (validFormat === false) {
    // Formato inválido
    return false;
  }

  // Formato válido
  return true;
};

/**
 * Normalizar caracteres reemplazando acentuados por
 * sus equivalentes no acentuados
 */
const normalizeCharacters = str => {
  /** 2021-07-06: Merlin no normaliza las ENIES vienen como ? */
  return str
    ? (str + "")
        .replace(/[?=]/g, "N")
        .replace(/[#=]/g, "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
    : str;
};

const obtenerDeviceId = function() {
  const uIdLocal = localStorage.getItem("uId");
  if (uIdLocal) {
    return uIdLocal;
  } else {
    const uId = uuidv4();
    localStorage.setItem("uId", uId);
    return uId;
  }
};

const obtenerDeviceIdTimestamp = function() {
  const timeStampLocal = localStorage.getItem("timeStamp");
  let date = new Date();
  let time = date.toTimeString();
  let timestamp;
  let gmt;
  date = date.toISOString().match(/\d{4}-\d{2}-\d{2}[A-Z]/g);
  gmt = time.match(/[-+]\d{2}\d{2}/g);
  time = time.match(/\d{2}:\d{2}:\d{2}/g);
  timestamp = date + time + gmt;
  if (timeStampLocal) {
    return timeStampLocal;
  } else {
    localStorage.setItem("timeStamp", timestamp);
    return timestamp;
  }
};
const calcularEdad = function(fechaNacimiento) {
  if (!fechaNacimiento) {
    return "";
  }

  const nacimiento = new Date(
    fechaNacimiento
      .split("/")
      .reverse()
      .join("-")
  );

  if (!(nacimiento instanceof Date) || isNaN(nacimiento)) {
    return "";
  }

  const hoy = new Date();
  const anios = hoy.getFullYear() - nacimiento.getFullYear();
  const mes = hoy.getMonth() - nacimiento.getMonth();
  if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
    return (anios - 1).toString();
  }
  return anios.toString();
};
const getEnvironment = () => {
  const currentHostname = window.location.hostname;
  const prodHosts = ["www.bbva.com.ar", "bbva.com.ar"];
  const testHosts = ["app-work.bbva.com.ar"];

  // Check if the current hostname matches any of the known hostnames
  if (prodHosts.includes(currentHostname)) {
    return {
      name: "prod",
      description: "Produccion",
      adobeLaunch:
        "//assets.adobedtm.com/95c3e405673d/81c80ecf6aaf/launch-144c155dcd64.min.js"
    };
  } else if (testHosts.includes(currentHostname)) {
    return {
      name: "qa",
      description: "Test",
      adobeLaunch:
        "//assets.adobedtm.com/95c3e405673d/81c80ecf6aaf/launch-8bd807155300-development.min.js"
    };
  }

  // No identificado? usar development
  return {
    name: "de",
    description: "Desarrollo",
    adobeLaunch:
      "//assets.adobedtm.com/95c3e405673d/81c80ecf6aaf/launch-8bd807155300-development.min.js"
  };
};
const mergeArrays = (array1, array2) => {
  const combinedObj = {
    ...array1,
    ...Object.fromEntries(
      Object.entries(array2).filter(
        ([key, value]) =>
          key !== null && value !== null && value !== undefined && value !== ""
      )
    )
  };
  return combinedObj;
};
const getValueByKey = (key, value, keyFind, arrayOfObjects, defaultValue) => {
  // No es un array, retornar
  if (!Array.isArray(arrayOfObjects)) {
    return defaultValue;
  }

  // Encontrar elemento que buscamos
  const obj = arrayOfObjects.find(item => item[key] === value);
  // Retornar el valor o bien el valor por defecto (o undefined)
  return obj ? obj[keyFind] : defaultValue;
};
const checkAndAddNoNumber = desDomi => {
  desDomi = desDomi.toUpperCase();

  //reemplazo cualquiera del array de strings por S/N
  const wordsToReplace = [" SIN NUMERO", " SINNUMERO", " S N"];

  if (wordsToReplace.some(word => desDomi.includes(word))) {
    let regex = new RegExp(wordsToReplace.join("|"), "g");
    return desDomi.replace(regex, " S/N");
  }

  //verifica que contenga numeros en los ultimos 6 caracteres maximo
  const numberRegex = /\d{1,6}(?=\D{0,5}$)/;
  if (!numberRegex.test(desDomi)) {
    if (desDomi.endsWith(" SN")) {
      //reemplaza el ultimo SN por S/N
      return desDomi.replace(/SN(?!(?:.*SN)+)/, "S/N");
    } else if (!desDomi.endsWith(" S/N")) {
      return desDomi + " S/N";
    }
  }

  return desDomi;
};

const identificarErrorFotoDNI = error => {
  const errorType =
    error.includes("Access token expired") ||
    error.includes("La verificaciÃ³n no estÃ¡ abierta")
      ? "error-tokenExpired"
      : "error-dni";

  return errorType;
};

const otpIdDevice = () => {
  let deviceID = MediaDeviceInfo.deviceId;
  if (typeof deviceID !== "undefined") {
    return deviceID;
  } else {
    let navigator_info = window.navigator;
    let screen_info = window.screen;
    let uid = navigator_info.mimeTypes.length; //NOSONAR
    uid += navigator_info.userAgent.replace(/\D+/g, "");
    uid += navigator_info.plugins.length; //NOSONAR
    uid += screen_info.height || "";
    uid += screen_info.width || "";
    uid += screen_info.pixelDepth || "";
    return uid;
  }
};
const isEmpty = value => {
  return value == undefined || value == null || value.trim() == "";
};

/**
 * Hashear en Sha256 un string utilizando la API de Crypto del navegador.
 * @param {String} value String a hashear con sha256
 * @returns {Promise<String>} - valor hasheado
 */
const hashSHA256 = async value => {
  try {
    const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;

    if (!value || !cryptoApi || !cryptoApi.subtle) {
      return "";
    }

    const encodedValue = new TextEncoder().encode(String(value));
    const hashBuffer = await cryptoApi.subtle.digest("SHA-256", encodedValue);

    return Array.from(new Uint8Array(hashBuffer))
      .map(byte => byte.toString(16).padStart(2, "0"))
      .join("");
  } catch (error) {
    return "";
  }
};

export {
  cuilGen,
  isValidDate,
  getParameterByName,
  getTrackingCodeLocal,
  obtenerDeviceId,
  obtenerDeviceIdTimestamp,
  obtenerTrackingCode,
  replaceAll,
  formatDinero,
  toTitleCase,
  isDev,
  get,
  isValidCuil,
  normalizeCharacters,
  calcularEdad,
  getEnvironment,
  mergeArrays,
  getValueByKey,
  checkAndAddNoNumber,
  identificarErrorFotoDNI,
  otpIdDevice,
  isEmpty,
  hashSHA256
};
